import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import ScheduleStepForm from "../AdminOnboarding/ScheduleStepForm";
import {
  addMinutesToTime,
  emptyScheduleDraft,
  findDraftTrainerSlotConflicts,
  findGroupTrainerSlotConflicts,
  formatGroupTrainerConflictMessage,
  groupTrainerConflictConfirmText,
  slotsToEntries,
} from "../AdminOnboarding/scheduleStepUtils";
import s from "../clinic.module.css";
import { useToast } from "../../ui";
import { useActivation } from "../../../activation";
import { useTrainingMode } from "../../../training";

export default function GroupSchedulePanel({
  groupId,
  groupName,
  directionName,
  onSaved,
  contextTrainerId = null,
}) {
  const toast = useToast();
  const {
    refresh: refreshActivation,
    visible: activationVisible,
    currentStep,
    currentSubstepId,
    completed: activationCompleted,
    advanceSubstep,
    finishCurrentStep,
    setSubstep,
  } = useActivation();
  const {
    active: trainingActive,
    currentLesson,
    trainingSubstepId,
    advanceTrainingSubstep,
    setTrainingSubstep,
    completed: trainingCompleted,
  } = useTrainingMode();
  const scheduleGuideActive =
    (activationVisible && currentStep?.id === "schedule") ||
    (trainingActive && currentLesson?.id === "schedule");
  const scheduleSubstepId = trainingActive
    ? trainingSubstepId
    : currentSubstepId;
  const scheduleGuideDone =
    (trainingActive && trainingCompleted.includes("schedule")) ||
    (activationVisible && activationCompleted.includes("schedule"));
  const [form, setForm] = useState(() => emptyScheduleDraft());
  const [trainers, setTrainers] = useState([]);
  const [defaultTrainerId, setDefaultTrainerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clinicAPI
      .getDoctors({ active: true })
      .then((r) => {
        setTrainers(r.doctors || r.trainers || []);
        setDefaultTrainerId(r.default_trainer_id || null);
      })
      .catch(() => {
        setTrainers([]);
        setDefaultTrainerId(null);
      });
  }, []);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getGroupScheduleSlots(groupId);
      const fallbackTrainerId =
        data.default_trainer_id || defaultTrainerId || contextTrainerId || null;
      const entries = slotsToEntries(data.slots || []).map((entry) => ({
        ...entry,
        trainer_id: entry.trainer_id || fallbackTrainerId || null,
      }));
      setForm({
        ...emptyScheduleDraft(),
        entries,
        start_time: entries[0]?.start_time || "16:00",
        duration_min: entries[0]?.duration_min || 60,
        draft_trainer_id: contextTrainerId || fallbackTrainerId || null,
      });
    } catch {
      setForm(emptyScheduleDraft());
    } finally {
      setLoading(false);
    }
  }, [groupId, contextTrainerId, defaultTrainerId]);

  useEffect(() => {
    load();
  }, [load]);

  // Пока слотов нет — подсказка «Дни и время», не «Сохранить»
  useEffect(() => {
    if (!scheduleGuideActive || scheduleGuideDone || loading) return;
    if (scheduleSubstepId !== "sched_save") return;
    if ((form.entries || []).length > 0) return;
    if (trainingActive) setTrainingSubstep?.("schedule", "sched_fill");
    else setSubstep?.("schedule", "sched_fill");
  }, [
    scheduleGuideActive,
    scheduleGuideDone,
    scheduleSubstepId,
    form.entries,
    loading,
    trainingActive,
    setTrainingSubstep,
    setSubstep,
  ]);

  // На телефоне кнопка «Сохранить» не должна быть под панелью тура
  useEffect(() => {
    if (!scheduleGuideActive || scheduleSubstepId !== "sched_save") return;
    const t = window.setTimeout(() => {
      document
        .querySelector('[data-act-sub="sched_save"]')
        ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 200);
    return () => window.clearTimeout(t);
  }, [scheduleGuideActive, scheduleSubstepId]);

  const save = async (e) => {
    e.preventDefault();
    const entries = form.entries || [];
    if (!entries.length) {
      toast.error("Добавьте хотя бы один слот в расписании");
      return;
    }

    if (contextTrainerId) {
      const conflicts = findGroupTrainerSlotConflicts(
        entries,
        contextTrainerId,
        trainers,
      );
      if (conflicts.length) {
        const ok = window.confirm(groupTrainerConflictConfirmText(conflicts));
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      const res = await clinicAPI.saveGroupScheduleSlots(groupId, {
        slots: entries.map((entry) => ({
          weekday: entry.weekday,
          start_time: entry.start_time,
          end_time: addMinutesToTime(entry.start_time, entry.duration_min),
          trainer_id: entry.trainer_id || undefined,
        })),
        generate_weeks: 8,
        regenerate_future: true,
      });
      toast.success(
        res.sessions_created
          ? `Расписание сохранено · ${res.sessions_created} занятий в календаре`
          : "Расписание сохранено",
      );
      if (
        scheduleGuideActive &&
        !scheduleGuideDone &&
        (scheduleSubstepId === "sched_save" ||
          scheduleSubstepId === "sched_fill")
      ) {
        if (trainingActive) advanceTrainingSubstep();
        else if (scheduleSubstepId === "sched_fill") finishCurrentStep();
        else advanceSubstep();
      }
      refreshActivation?.();
      onSaved?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className={s.groupsHubScheduleHint}>Загрузка расписания…</p>;
  }

  return (
    <div
      className={s.groupsHubSchedulePanel}
      data-act-tour={scheduleGuideActive && !scheduleGuideDone ? "1" : undefined}
    >
      <p className={s.groupsHubScheduleHint}>
        {contextTrainerId
          ? "Настройте слоты этой группы для выбранного тренера. Если время совпадает с занятием другого тренера — покажем предупреждение."
          : "Здесь задаётся только время занятий группы. Кто ведёт занятие — назначается в карточке тренера (вкладка «Расписание») или в колонке «Тренер» у каждого слота. При сохранении будущие занятия группы пересоздаются на 8 недель."}
      </p>
      <ScheduleStepForm
        form={form}
        onChange={setForm}
        onSubmit={save}
        saving={saving}
        submitLabel="Сохранить расписание"
        groupName={groupName}
        directionName={directionName}
        trainers={trainers}
        contextTrainerId={contextTrainerId}
        defaultTrainerId={defaultTrainerId}
        submitEnabled={(form.entries || []).length > 0}
      />
    </div>
  );
}
